import { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';

// Interface for user permissions
interface UserPermissions {
  canAccessPhotoSources: boolean;
  canAccessVideoSources: boolean;
  canAccessAssetLibrary: boolean;
  userRoles: string[];
}

/**
 * AssetManagement Component
 * 
 * Layout component for Asset Management module with three sub-modules:
 * 1. Photo Sources - Browse and select photos from DAM and Impact App
 * 2. Video Sources - Manage video assets from various sources
 * 3. Asset Library - Curated collection of approved assets for storytelling
 * 
 * Role-based access control ensures users can only access modules they have permission for.
 * Now works with nested routing instead of tabs.
 */
export const AssetManagement = () => {
  const [userPermissions, setUserPermissions] = useState<UserPermissions>({
    canAccessPhotoSources: false,
    canAccessVideoSources: false,
    canAccessAssetLibrary: false,
    userRoles: []
  });

  const { user, isAuthenticated, isLoading } = useAuth();

  // Update permissions when user changes
  useEffect(() => {
    if (!user || !isAuthenticated) {
      return;
    }

    const roles = user.roles || [];
    const permissions: UserPermissions = {
      canAccessPhotoSources: roles.some(role => 
        role.startsWith('IMPACT_ASSET.') || 
        role === 'ADMIN' || 
        role.includes('PHOTO_TEAM') || 
        role.includes('MARKETING')
      ),
      canAccessVideoSources: roles.some(role => 
        role.startsWith('IMPACT_ASSET.') || 
        role === 'ADMIN' || 
        role.includes('VIDEO_TEAM') || 
        role.includes('MARKETING')
      ),
      canAccessAssetLibrary: roles.some(role => 
        role.startsWith('IMPACT_ASSET.') || 
        role === 'ADMIN' || 
        role.includes('MARKETING')
      ),
      userRoles: roles
    };

    setUserPermissions(permissions);
  }, [user, isAuthenticated]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rgf-primary-600"></div>
      </div>
    );
  }

  // Check if user has any permissions
  const hasAnyPermissions = userPermissions.canAccessPhotoSources || 
                           userPermissions.canAccessVideoSources || 
                           userPermissions.canAccessAssetLibrary;

  if (!hasAnyPermissions) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="mt-4 text-lg font-medium text-gray-900">Access Denied</h3>
          <p className="mt-2 text-sm text-gray-500">
            You don't have permission to access the asset management system. 
            Please contact your administrator for access.
          </p>
          <div className="mt-6">
            <Button
              onClick={() => window.location.href = '/'}
              variant="secondary"
            >
              Return to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gray-50">
      {/* Main Content - Outlet for nested routes */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </div>

      {/* User Permissions Debug Info (only in development) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed bottom-4 right-4 bg-gray-800 text-white p-4 rounded-lg text-xs max-w-xs">
          <div className="font-medium mb-2">Debug: User Permissions</div>
          <div>Photo Sources: {userPermissions.canAccessPhotoSources ? '✅' : '❌'}</div>
          <div>Video Sources: {userPermissions.canAccessVideoSources ? '✅' : '❌'}</div>
          <div>Asset Library: {userPermissions.canAccessAssetLibrary ? '✅' : '❌'}</div>
          <div className="mt-2 text-xs opacity-75">
            Roles: {userPermissions.userRoles.join(', ')}
          </div>
        </div>
      )}
    </div>
  );
};